import { Component, OnInit, Input, ViewChild, ElementRef } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ViewportScroller } from '@angular/common';

import {
  ScrollToService,
  ScrollToConfigOptions,
} from '@nicky-lenaers/ngx-scroll-to';
import { fromEvent, Observable, Subscription } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import {
  MetricsService,
  MetricType,
  GetMetricsTokenAndURLResponse,
  GetMetricsTokenAndURLParams,
} from 'src/app/core';

@UntilDestroy()
@Component({
  selector: 'app-oxio-metrics',
  templateUrl: './oxio-metrics.component.html',
  styleUrls: ['./oxio-metrics.component.less'],
})
export class OXIOMetricsComponent implements OnInit {
  iFrameUrl: SafeResourceUrl;
  private _brandUuid: string;

  @ViewChild('oxioMetricsIFrame') oxioMetricsIFrame: ElementRef<HTMLElement>;
  @Input() phoneNumber: string;
  @Input() lineId: string;
  @Input() dashboard: MetricType;
  @Input() set brandUuid(brandUuid: string) {
    const oldBrandUuid = this.brandUuid;
    this._brandUuid = brandUuid;

    if (brandUuid !== oldBrandUuid && typeof this.dashboard !== 'undefined') {
      this.runMetrics();
    }
  }

  get brandUuid() {
    return this._brandUuid;
  }

  get isBrandIqDashboard(): boolean {
    return (
      this.dashboard === MetricType.BRANDIQ_BASIC_DATA_COLLECTION_FUNNEL ||
      this.dashboard === MetricType.BRANDIQ_BASIC_APP_INSIGHTS
    );
  }

  HEIGHT_BUMPER = 75;
  oxioMetricsHeight = 1200;

  resizeObservable$: Observable<Event>;
  resizeSubscription$: Subscription;
  loadObservable$: Observable<Event>;
  loadSubscription$: Subscription;

  constructor(
    private domSanitzer: DomSanitizer,
    private metricsService: MetricsService,
    private viewportScroller: ViewportScroller,
    private scrollToService: ScrollToService
  ) {}

  ngOnInit(): void {
    this.runMetrics();
    if (!this.isBrandIqDashboard) this.resizeToWindow();
  }

  resizeToWindow() {
    this.triggerScrollTo();
    this.oxioMetricsHeight = window.innerHeight - this.HEIGHT_BUMPER;

    this.resizeObservable$ = fromEvent(window, 'resize');
    this.resizeSubscription$ = this.resizeObservable$
      .pipe(untilDestroyed(this))
      .subscribe((evt: any) => {
        this.oxioMetricsHeight = evt.target.innerHeight - this.HEIGHT_BUMPER;
      });
  }

  runMetrics() {
    let param = {} as GetMetricsTokenAndURLParams;

    if (this.dashboard === MetricType.END_USER_METRICS) {
      if (this.lineId) {
        param.lineId = this.lineId;
      } else if (this.phoneNumber) {
        param.phoneNumber = parseInt(this.phoneNumber).toString();
      } else {
        throw new Error(
          `Must assign a phoneNumber or lineId input value to component MetricsComponent when metric type is ${this.dashboard}`
        );
      }
    } else if (this.isBrandIqDashboard) {
      if (this.brandUuid) {
        param.brandUuid = this.brandUuid;
      } else {
        throw new Error(
          `Must assign a brandUuid input value to component MetricsComponent when metric type is ${this.dashboard}`
        );
      }
    } else if (typeof this.dashboard !== 'undefined') {
      if (this.brandUuid) {
        param.brandUuid = this.brandUuid;
      } else {
        throw new Error(
          `Must assign a brandUuid input value to component MetricsComponent when metric type is ${this.dashboard}`
        );
      }
    } else {
      throw new Error('Must provide a Metric dashboard type');
    }

    param.dashboard = this.dashboard;
    this.getMetricsTokenAndURL(param);
  }

  getMetricsTokenAndURL(param) {
    this.metricsService
      .getMetricsTokenAndURL(param)
      .pipe(untilDestroyed(this))
      .subscribe(
        (res: GetMetricsTokenAndURLResponse) => {
          this.iFrameUrl = this.buildURL(res.embedCode, res.token);
        },
        () => {
          throw new Error('Failed while fetching metrics url parameters');
        }
      );
  }

  triggerScrollTo() {
    const config: ScrollToConfigOptions = {
      target: 'oxio-metrics-iframe-target',
    };

    this.scrollToService
      .scrollTo(config)
      .pipe(untilDestroyed(this))
      .subscribe(
        (value: number) => {
          this.viewportScroller.scrollToPosition([0, value]);
        },
        (err) => console.error(err) // Error is caught and logged instead of thrown
      );
  }

  buildURL(embedCode: string, token: string): SafeResourceUrl {
    //github.com/angular/angular/blob/10.1.3/packages/platform-browser/src/security/dom_sanitization_service.ts#L135-L143
    https: return this.domSanitzer.bypassSecurityTrustResourceUrl(
      `https://secure.holistics.io/embed/${embedCode}?_token=${token}`
    );
  }
}
